import { COMPETENCY_TAG_PATH_PREFIX } from '@features/kt/constants/tag.conts';

import {
  type ActivityPedagogicalFieldResponse,
  type PedagogicalFieldGroup,
  PedagogicalFieldType,
  type Tag,
} from '@api';

import { type CardDisplayMenuFieldInputKind } from './activity-edit-field-layout.utils';
import {
  PEDAGOGICAL_FIELD_CONTENT_GOAL,
  PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED,
  pedagogicalFieldPairPartner,
} from './activity-edit-pedagogical.constants';

/** One visible card row. `contentGoalWaived` rides on `contentGoal` instead of its own row. */
export interface PedagogicalCardField {
  readonly field: ActivityPedagogicalFieldResponse;
  readonly waived: ActivityPedagogicalFieldResponse | null;
}

export interface PedagogicalMenuFieldRow {
  readonly fieldName: string;
  readonly visible: boolean;
  readonly sortOrder: number;
  readonly type: PedagogicalFieldType;
  readonly group: PedagogicalFieldGroup;
}

/** Result of moving one gear-menu row. `position` is 1-based in the menu list. */
export interface PedagogicalMenuMove {
  readonly fieldName: string;
  readonly fieldNames: readonly string[];
  readonly position: number;
  readonly total: number;
}

/** Body for `POST …/pedagogical-fields:reorder` (`position` is 1-based in the full pedagogical list). */
export interface PedagogicalReorderRequest {
  readonly fieldName: string;
  readonly position: number;
}

/** One gear-menu row the reorder helpers can walk. Every pedagogical row can move. */
export interface PedagogicalReorderRow {
  readonly fieldName: string;
}

type PedagogicalSortableRow = Pick<PedagogicalMenuFieldRow, 'fieldName' | 'sortOrder'>;

/**
 * Merges a visibility/reorder list response into the draft list: updates `visible` and `sortOrder`
 * for known `fieldName`s, appends unknown rows, keeps `value` / `type` / `options` from the draft.
 *
 * @param current - Draft pedagogical fields before the server response.
 * @param serverItems - Full list returned by the visibility or reorder endpoint.
 * @returns Merged list sorted by `sortOrder`.
 */
export function mergePedagogicalFieldListFromServer(
  current: readonly ActivityPedagogicalFieldResponse[],
  serverItems: readonly ActivityPedagogicalFieldResponse[],
): ActivityPedagogicalFieldResponse[] {
  const currentByName = new Map(current.map((item) => [item.fieldName, item]));
  const merged: ActivityPedagogicalFieldResponse[] = [];

  for (const server of serverItems) {
    const existing = currentByName.get(server.fieldName);
    if (existing) {
      merged.push({
        ...existing,
        visible: server.visible,
        sortOrder: server.sortOrder,
        group: server.group,
      });
      currentByName.delete(server.fieldName);
      continue;
    }
    merged.push({ ...server });
  }

  for (const leftover of currentByName.values()) {
    merged.push({ ...leftover });
  }

  return merged.toSorted((a, b) => comparePedagogicalFieldRows(a, b));
}

/**
 * Every pedagogical field name in `sortOrder`, including rows omitted from the gear menu.
 *
 * @param items - Full pedagogical field list for the instance.
 * @returns Names in full-list `sortOrder` (used for optimistic moves and tests).
 */
export function orderedPedagogicalFieldNames(items: readonly ActivityPedagogicalFieldResponse[]): string[] {
  return [...items].toSorted((left, right) => comparePedagogicalFieldRows(left, right)).map((item) => item.fieldName);
}

/**
 * Competency-style tag for one pedagogical enum value on the detail panel.
 *
 * @param optionCode - Stored enum code.
 * @param optionLabel - Translated option label.
 * @returns One tag for the chip list, or empty when the code is absent.
 */
export function pedagogicalEnumDetailTags(optionCode: string, optionLabel: string): Tag[] {
  if (!optionCode) {
    return [];
  }

  return [
    {
      label: optionLabel,
      path: `${COMPETENCY_TAG_PATH_PREFIX}${optionCode}`,
    },
  ];
}

/**
 * Whether a stored pedagogical value should render on the read-only detail panel.
 *
 * @param field - One pedagogical field from the instance API.
 * @returns True when the stored value has teacher-visible content.
 */
export function pedagogicalFieldHasDisplayValue(field: ActivityPedagogicalFieldResponse): boolean {
  const value = field.value;
  if (value === null) {
    return false;
  }

  switch (field.type) {
    case PedagogicalFieldType.BOOLEAN: {
      return value === 'true';
    }
    case PedagogicalFieldType.TEXT_INPUT:
    case PedagogicalFieldType.TEXT_AREA:
    case PedagogicalFieldType.ENUM: {
      return value.trim().length > 0;
    }
    case PedagogicalFieldType.HTML: {
      return htmlHasDisplayContent(value);
    }
    default: {
      return false;
    }
  }
}

/**
 * Detail panel rows: visible fields with a displayable value, in `sortOrder`.
 *
 * @param items - Full pedagogical field list for the instance.
 * @returns Card rows that should render in the detail panel.
 */
export function pedagogicalFieldsForDetailPanel(
  items: readonly ActivityPedagogicalFieldResponse[],
): PedagogicalCardField[] {
  return visiblePedagogicalFieldsForCard(items).filter((row) => pedagogicalRowHasDisplayValue(row));
}

/**
 * Gear-menu rows in `sortOrder`. Hides `contentGoalWaived` (paired with `contentGoal`).
 *
 * @param items - Full pedagogical field list for the instance.
 * @returns Rows for the gear visibility menu.
 */
export function pedagogicalFieldsForMenu(
  items: readonly ActivityPedagogicalFieldResponse[],
): PedagogicalMenuFieldRow[] {
  return items
    .filter((item) => item.fieldName !== PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED)
    .map((item) => toMenuRow(item))
    .toSorted((a, b) => comparePedagogicalFieldRows(a, b));
}

/**
 * Material Symbols name for the gear-menu type hint on a pedagogical or custom field row.
 *
 * @param type - Wire `PedagogicalFieldType`.
 * @returns Icon ligature name (outlined set).
 */
export function pedagogicalFieldTypeMenuIcon(type: PedagogicalFieldType): string {
  switch (type) {
    case PedagogicalFieldType.TEXT_INPUT: {
      return 'short_text';
    }
    case PedagogicalFieldType.TEXT_AREA: {
      return 'notes';
    }
    case PedagogicalFieldType.BOOLEAN: {
      return 'check_box';
    }
    case PedagogicalFieldType.ENUM: {
      return 'dropdown_menu';
    }
    case PedagogicalFieldType.HTML: {
      return 'article';
    }
  }
}

/**
 * 1-based index of `movedFieldName` in the full pedagogical list after a gear-menu reorder.
 *
 * @param items - Current pedagogical field list.
 * @param orderedMenuFieldNames - Menu rows in the new order (`contentGoalWaived` omitted).
 * @param movedFieldName - Field that was dragged or moved with the keyboard.
 * @returns Target `position` for the reorder API.
 */
export function pedagogicalFullListPositionAfterMenuReorder(
  items: readonly ActivityPedagogicalFieldResponse[],
  orderedMenuFieldNames: readonly string[],
  movedFieldName: string,
): number {
  const optimistic = withPedagogicalMenuOrderOptimistic(items, orderedMenuFieldNames);
  const index = orderedPedagogicalFieldNames(optimistic).indexOf(movedFieldName);
  return index === -1 ? 1 : index + 1;
}

/**
 * Menu field-name order after a drop in the pedagogical gear list (every GET row except
 * `contentGoalWaived`, which is not a menu row).
 *
 * @param rows - Gear-menu rows in display order.
 * @param previousIndex - Index of the dragged row before the drop.
 * @param currentIndex - Index where the row was dropped.
 * @returns The new menu-name order, or `null` when the order does not change.
 */
export function pedagogicalMenuFieldNamesAfterDrop(
  rows: readonly PedagogicalReorderRow[],
  previousIndex: number,
  currentIndex: number,
): string[] | null {
  if (previousIndex === currentIndex) {
    return null;
  }

  const names = rows.map((row) => row.fieldName);
  if (previousIndex < 0 || currentIndex < 0 || previousIndex >= names.length || currentIndex >= names.length) {
    return null;
  }

  return moveFieldName(names, previousIndex, currentIndex);
}

/**
 * Menu field-name order after ArrowUp (`-1`) or ArrowDown (`1`).
 *
 * @param rows - Gear-menu rows in display order.
 * @param fieldName - Field that owns the focused handle.
 * @param direction - `-1` toward the previous row, `1` toward the next.
 * @returns The move, or `null` when the field is missing or already at that end.
 */
export function pedagogicalMenuFieldNamesAfterKeyMove(
  rows: readonly PedagogicalReorderRow[],
  fieldName: string,
  direction: -1 | 1,
): PedagogicalMenuMove | null {
  const names = rows.map((row) => row.fieldName);
  const from = names.indexOf(fieldName);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= names.length) {
    return null;
  }

  const fieldNames = moveFieldName(names, from, to);
  return { fieldName, fieldNames, position: to + 1, total: fieldNames.length };
}

/**
 * Gear-menu hint for a pedagogical field type (picker vs free entry).
 *
 * @param type - Wire `PedagogicalFieldType`.
 * @returns `choice` for enum/boolean fields, otherwise `text`.
 */
export function pedagogicalMenuInputKind(type: PedagogicalFieldType): CardDisplayMenuFieldInputKind {
  if (type === PedagogicalFieldType.ENUM || type === PedagogicalFieldType.BOOLEAN) {
    return 'choice';
  }

  return 'text';
}

/**
 * Row id used when emitting visibility for the content-goal pair (always `contentGoal`).
 *
 * @param fieldName - Toggled field name from the menu.
 * @returns Canonical field name sent to the visibility API.
 */
export function pedagogicalVisibilityToggleFieldName(fieldName: string): string {
  if (fieldName === PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED) {
    return PEDAGOGICAL_FIELD_CONTENT_GOAL;
  }
  return fieldName;
}

/**
 * Visible card rows in `sortOrder`. Hidden rows stay out of the DOM.
 * `contentGoalWaived` is attached to `contentGoal` when that partner is also visible.
 *
 * @param items - Full pedagogical field list for the instance.
 * @returns Visible edit-card rows with optional waived companion.
 */
export function visiblePedagogicalFieldsForCard(
  items: readonly ActivityPedagogicalFieldResponse[],
): PedagogicalCardField[] {
  const byName = new Map(items.map((item) => [item.fieldName, item]));

  return items
    .filter((item) => item.visible && item.fieldName !== PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED)
    .map((item) => {
      const partner = byName.get(PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED);
      const waived = item.fieldName === PEDAGOGICAL_FIELD_CONTENT_GOAL && partner?.visible === true ? partner : null;

      return { field: item, waived };
    })
    .toSorted((a, b) => comparePedagogicalFieldRows(a.field, b.field));
}

/**
 * Optimistic full-list move for one field to `position` (1-based), matching the server reorder rule.
 *
 * @param items - Current pedagogical field list.
 * @param fieldName - Field to move.
 * @param position - Target slot in the full list (1…n).
 * @returns Updated list with new `sortOrder` values, or the input when the move is a no-op.
 */
export function withPedagogicalFieldAtPosition(
  items: readonly ActivityPedagogicalFieldResponse[],
  fieldName: string,
  position: number,
): ActivityPedagogicalFieldResponse[] {
  const names = orderedPedagogicalFieldNames(items);
  const from = names.indexOf(fieldName);
  if (from === -1 || position < 1 || position > names.length) {
    return [...items];
  }

  const to = position - 1;
  if (from === to) {
    return [...items];
  }

  const reordered = moveFieldName(names, from, to);
  const sortOrderByName = new Map(reordered.map((name, index) => [name, index + 1]));

  return items.map((item) => {
    const sortOrder = sortOrderByName.get(item.fieldName);
    return sortOrder === undefined || sortOrder === item.sortOrder ? item : { ...item, sortOrder };
  });
}

/**
 * Applies a gear-menu reorder onto the draft list and keeps `contentGoalWaived` directly after
 * `contentGoal` when both exist.
 *
 * @param items - Current pedagogical field list.
 * @param orderedMenuFieldNames - Menu rows in the new order.
 * @returns List with updated `sortOrder` values.
 */
export function withPedagogicalMenuOrderOptimistic(
  items: readonly ActivityPedagogicalFieldResponse[],
  orderedMenuFieldNames: readonly string[],
): ActivityPedagogicalFieldResponse[] {
  const ordered = [...orderedMenuFieldNames];
  const goalIndex = ordered.indexOf(PEDAGOGICAL_FIELD_CONTENT_GOAL);
  const hasWaived = items.some((item) => item.fieldName === PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED);
  if (goalIndex !== -1 && hasWaived && !ordered.includes(PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED)) {
    ordered.splice(goalIndex + 1, 0, PEDAGOGICAL_FIELD_CONTENT_GOAL_WAIVED);
  }

  const sortOrderByName = new Map(ordered.map((fieldName, index) => [fieldName, index + 1]));

  return items.map((item) => {
    const sortOrder = sortOrderByName.get(item.fieldName);
    return sortOrder === undefined || sortOrder === item.sortOrder ? item : { ...item, sortOrder };
  });
}

/**
 * Copies `sortOrder` from a settled list onto the current draft. Values and visibility stay.
 *
 * @param current - Draft list, including optimistic edits.
 * @param baseline - Last settled list.
 * @returns Draft list with baseline `sortOrder` where the field is known.
 */
export function withPedagogicalSortOrderFromBaseline(
  current: readonly ActivityPedagogicalFieldResponse[],
  baseline: readonly ActivityPedagogicalFieldResponse[],
): ActivityPedagogicalFieldResponse[] {
  const sortOrderByName = new Map(baseline.map((item) => [item.fieldName, item.sortOrder]));

  return current.map((item) => {
    const sortOrder = sortOrderByName.get(item.fieldName);
    return sortOrder === undefined || sortOrder === item.sortOrder ? item : { ...item, sortOrder };
  });
}

/**
 * Applies an optimistic visibility toggle, including the content goal (Tartalmi cél) pair.
 *
 * @param items - Current pedagogical field list.
 * @param fieldName - Field whose visibility changed.
 * @param visible - New visibility flag.
 * @returns Updated list with the toggle (and pair partner) applied.
 */
export function withPedagogicalVisibilityOptimistic(
  items: readonly ActivityPedagogicalFieldResponse[],
  fieldName: string,
  visible: boolean,
): ActivityPedagogicalFieldResponse[] {
  const partner = pedagogicalFieldPairPartner(fieldName);
  const namesToUpdate = new Set([fieldName, ...(partner ? [partner] : [])]);

  return items.map((item) => (namesToUpdate.has(item.fieldName) ? { ...item, visible } : item));
}

/**
 * Sorts pedagogical rows by catalogue `sortOrder`, then `fieldName`.
 *
 * @param a - First row.
 * @param b - Second row.
 * @returns Comparison result for `Array.prototype.toSorted`.
 */
function comparePedagogicalFieldRows(a: PedagogicalSortableRow, b: PedagogicalSortableRow): number {
  return a.sortOrder - b.sortOrder || a.fieldName.localeCompare(b.fieldName);
}

/**
 * Whether HTML field content has non-whitespace text after stripping markup.
 *
 * @param html - Stored rich-text value.
 * @returns True when visible text remains.
 */
function htmlHasDisplayContent(html: string): boolean {
  const trimmed = html.trim();
  if (trimmed.length === 0) {
    return false;
  }

  return (new DOMParser().parseFromString(trimmed, 'text/html').body.textContent?.trim().length ?? 0) > 0;
}

/**
 * Moves one name to a new index. `to` is the final index, matching CDK `moveItemInArray`.
 *
 * @param names - Names before the move.
 * @param from - Current index.
 * @param to - Final index.
 * @returns A new list.
 */
function moveFieldName(names: readonly string[], from: number, to: number): string[] {
  const next = [...names];
  const moved = next[from];
  next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * Whether a visible card row should appear on the read-only detail panel.
 *
 * @param row - Card row with optional content-goal waived companion.
 * @returns True when the primary or waived field has displayable content.
 */
function pedagogicalRowHasDisplayValue(row: PedagogicalCardField): boolean {
  if (pedagogicalFieldHasDisplayValue(row.field)) {
    return true;
  }

  if (row.field.fieldName === PEDAGOGICAL_FIELD_CONTENT_GOAL && row.waived) {
    return pedagogicalFieldHasDisplayValue(row.waived);
  }

  return false;
}

/**
 * Maps one API field to a gear-menu row DTO.
 *
 * @param item - Pedagogical field from the instance API.
 * @returns Menu row without the waived companion field.
 */
function toMenuRow(item: ActivityPedagogicalFieldResponse): PedagogicalMenuFieldRow {
  return {
    fieldName: item.fieldName,
    visible: item.visible,
    sortOrder: item.sortOrder,
    type: item.type,
    group: item.group,
  };
}

export { cardMenuInputKindLabelKey as pedagogicalMenuInputKindLabelKey } from './activity-edit-field-layout.utils';
